// Plain words for statuses, blocked reasons, events, and scheduled states. One place, so the
// wording can be changed without touching the screens.

import { errorCodeText, formatPhone } from '@bt/core';
import type { Message, ConversationEvent, ScheduledMessage, UserSummary } from '../api';
import { fmtTime, zoneLabel, displayName } from './format';

export const BLOCKED_WORDS: Record<string, string> = {
  opted_out: 'Not sent: this number opted out',
  quiet_hours: 'Not sent: outside texting hours',
  repetition: 'Not sent: this same text has gone out too many times today',
  line_paused: 'Not sent: the line is paused',
  line_inactive: 'Not sent: the line is switched off',
  company_name_missing: 'Not sent: set the company name in Settings',
  superseded: 'Not sent: replaced by the copy sent with "Send now"',
};

export function blockedWords(reason: string | null | undefined): string {
  if (!reason) return 'Not sent';
  return BLOCKED_WORDS[reason] ?? `Not sent: ${reason.replace(/_/g, ' ')}`;
}

export const DELIVERED_TOOLTIP = 'The carrier accepted it. Spam filters can still hide it.';

export interface StatusWords {
  text: string;
  tone: 'faint' | 'muted' | 'danger';
  tooltip?: string;
  canResend: boolean;
}

/** The little line under an outbound bubble. `zoneTz` = the customer's zone for "will send at". */
export function outboundStatus(m: Message, zoneTz: string, workspaceTz: string, now = new Date()): StatusWords {
  const notBefore = m.not_before ? new Date(m.not_before) : null;
  switch (m.status) {
    case 'queued':
      if (notBefore && notBefore.getTime() > now.getTime()) {
        const zone = zoneTz !== workspaceTz ? ` ${zoneLabel(zoneTz)}` : '';
        return { text: `Will send at ${fmtTime(m.not_before, zoneTz)}${zone}`, tone: 'muted', canResend: false };
      }
      return { text: 'Queued', tone: 'faint', canResend: false };
    case 'sending':
      return { text: 'Sending', tone: 'faint', canResend: false };
    case 'accepted':
      return { text: 'On the phone', tone: 'faint', canResend: false };
    case 'sent':
      return { text: 'Sent', tone: 'faint', canResend: false };
    case 'delivered':
      return { text: 'Delivered', tone: 'faint', tooltip: DELIVERED_TOOLTIP, canResend: false };
    case 'failed': {
      const words = errorCodeText(m.provider_error_code) || 'Failed';
      return { text: words, tone: 'danger', tooltip: m.provider_error_detail ?? undefined, canResend: true };
    }
    case 'expired':
      return { text: errorCodeText('EXPIRED'), tone: 'danger', canResend: true };
    case 'blocked':
      return { text: blockedWords(m.blocked_reason), tone: 'muted', canResend: false };
    default:
      return { text: m.status, tone: 'faint', canResend: false };
  }
}

function who(u: UserSummary | null | undefined, id: string | null | undefined, users: UserSummary[]): string {
  if (u?.name) return u.name;
  if (id) return users.find((x) => x.id === id)?.name ?? 'Someone';
  return 'Someone';
}

/** One grey line for a non-note event. */
export function eventWords(e: ConversationEvent, users: UserSummary[]): string {
  const actor = who(e.user, e.user_id, users);
  const d = e.data ?? {};
  const target = (d.user_id ?? d.assigned_user_id ?? d.to_user_id) as string | undefined;
  const targetName = (d.user_name ?? d.assigned_user_name) as string | undefined;
  switch (e.type) {
    case 'assigned': {
      const to = targetName ?? (target ? users.find((u) => u.id === target)?.name : undefined) ?? 'someone';
      return `${actor} assigned this to ${to}`;
    }
    case 'unassigned':
      return d.reason === 'user_disabled' ? 'Unassigned (user disabled)' : `${actor} unassigned this`;
    case 'closed':
      return `${actor} closed the conversation`;
    case 'reopened':
      return e.user_id ? `${actor} reopened the conversation` : 'Reopened (new text)';
    case 'opted_out':
      return 'Opted out';
    case 'opted_in':
      return 'Opted back in';
    case 'opt_out_suspected':
      return 'Flagged: this may be an opt-out request';
    case 'opt_out_dismissed':
      return `${actor} marked this as not an opt-out`;
    case 'self_test':
      return `Self-test${e.body ? `: ${e.body}` : ''}`;
    case 'line_changed':
      return e.body ?? 'The texting line changed';
    case 'quiet_hours_override':
      return `${actor} sent during quiet hours${e.body ? `: ${e.body}` : ''}`;
    case 'system':
      return e.body ?? 'System';
    default:
      return e.body ?? e.type;
  }
}

export const SCHEDULED_WORDS: Record<ScheduledMessage['status'], string> = {
  pending: 'Pending',
  held: 'Held',
  queued: 'Queued',
  sent: 'Sent',
  cancelled: 'Cancelled',
  skipped_opted_out: 'Skipped: opted out',
  failed: 'Failed',
};

export function scheduledTone(s: ScheduledMessage['status']): 'ok' | 'warn' | 'danger' | '' {
  if (s === 'sent') return 'ok';
  if (s === 'held' || s === 'queued') return 'warn';
  if (s === 'failed' || s === 'skipped_opted_out') return 'danger';
  return '';
}

export function contactLabel(c: { name?: string | null; phone_number: string } | null | undefined): string {
  return displayName(c);
}

/** The locked-composer sentence from §11.3. */
export function lockedComposerText(
  contact: { name?: string | null; phone_number: string },
  optOut: { reason?: string; created_at?: string } | null | undefined,
  tz: string,
): string {
  const name = displayName(contact);
  const when = optOut?.created_at ? ` on ${new Date(optOut.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: tz })} at ${fmtTime(optOut.created_at, tz)}` : '';
  const how = optOut?.reason === 'manual' ? 'was blocked' : optOut?.reason === 'provider' ? 'opted out through the carrier' : 'opted out by text';
  const number = formatPhone(contact.phone_number);
  return `${name} ${how}${when}. You can't text them. Call ${number}. If they ask to opt back in, use Unblock in the contact panel.`;
}
